import { PageHeader } from "@layouts/PageHeader";
import { Header } from "./Header";
import { UICardWrap } from "./UICardWrap";
import { Alert, message } from "antd";
import { Trans } from "react-i18next";
import { IconNotice } from "@icons/index";
import { useCallback, useState } from "react";
import { useSetAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { notificationModalAtom } from "@src/jotai/notification";
import { getNotificationPermission, sendBrowserNotification } from "@utils/notification";
import { sendVideoCompletedNotification } from "@hooks/useVideoGenerationNotification";
import { useAppSelector } from "@hooks/redux";
import { selectProcessingItems, selectvideoGeneratedList } from "./librarySlice";
import type { UserAssetRecord } from "@apis/http/api";
import GlobalVideoDetailModal from "@layouts/GlobalVideoDetailModal";

export const Library = () => {
  const [searchParams] = useSearchParams();
  const useDebug = searchParams.get("useDebug") === "true";
  const setNotificationModal = useSetAtom(notificationModalAtom);
  const [notificationGranted, setNotificationGranted] = useState(() => getNotificationPermission() === "granted");
  const processingTasks = useAppSelector(selectProcessingItems);
  const videoGeneratedList = useAppSelector(selectvideoGeneratedList);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalIndex, setDetailModalIndex] = useState(0);

  const handleVideoDetailClick = useCallback((_record: UserAssetRecord, index: number) => {
    setDetailModalIndex(index);
    setDetailModalOpen(true);
  }, []);

  return (
    <div className="flex flex-col h-screen">
      <PageHeader title="Library" showBack={false}></PageHeader>
      {!notificationGranted && processingTasks.length > 0 && (
        <Alert
          banner
          closable
          icon={<IconNotice className="w-[16px] h-[16px]" />}
          type="info"
          className="bg-kds-bg-subtle! text-kds-fg!"
          title={
            <Trans
              components={{
                span: (
                  <span
                    className="font-bold cursor-pointer hover:underline"
                    onClick={() => {
                      setNotificationModal({
                        open: true,
                        onComplete: () => {
                          if (getNotificationPermission() === "granted") {
                            setNotificationGranted(true);
                          }
                        },
                      });
                    }}
                  />
                ),
              }}
              defaults="Don't wait! Start a new task. <span>Enable notifications</span> to know when it's done."
            ></Trans>
          }
        />
      )}
      {/* 通知测试：仅 useDebug=true 时显示 */}
      {useDebug && (
        <button
          className="m-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => {
            if (!("Notification" in window)) {
              message.warning("此浏览器不支持桌面通知");
              return;
            }
            if (Notification.permission === "granted") {
              message.info("3 秒后发送通知。Mac 上请先切换到其他窗口或最小化浏览器，才能看到弹窗", 4);
              setTimeout(() => {
                sendVideoCompletedNotification({
                  aspectRatio: null,
                  context: {
                    coverImageUrl:
                      "https://resource.kaloclip.com/resources/1470811/generate/vc_20260210062319254_507361_605c/ffe3894e-4ede-4f84-8169-6a0d178dcffe.jpg",
                    videoUrl:
                      "https://resource.kaloclip.com/temporary/1470811/generate/vc_20260210062319254_507361_605c/ffe3894e-4ede-4f84-8169-6a0d178dcffe.mp4",
                  },
                  copywriting: "Video Creation",
                  createdAt: 1770704599263,
                  duration: null,
                  errorMessage: null,
                  id: "vc_20260210062319254_507361_605c",
                  jobType: "VIDEO_CREATION",
                  productTitle:
                    "[Dr.Melaxin Official] Gifted Collagen Boost Set Pore Spot Skin Care Calcium Collagen Volume Firming Balm for Enhanced Skin Health routine",
                  quantity: null,
                  resolution: null,
                  resourceId: "vc_20260210062319254_507361_605c",
                  status: "completed",
                } as any);
                message.success("已发送。若未看到弹窗：点击 Mac 右上角「日期时间」打开「通知中心」查看", 6);
              }, 3000);
            } else if (Notification.permission !== "denied") {
              message.info("正在请求通知权限…");
              Notification.requestPermission().then((permission) => {
                if (permission === "granted") {
                  message.info("3 秒后发送通知。Mac 上请先切换到其他窗口或最小化浏览器，才能看到弹窗", 4);
                  setTimeout(() => {
                    const notification = sendBrowserNotification("你好呀！", {
                      body: "这是一条测试通知，点击可聚焦当前页面",
                    });
                    if (notification) {
                      notification.onclick = () => {
                        window.focus();
                        notification.close();
                      };
                      message.success("已发送。若未看到弹窗：点击 Mac 右上角「日期时间」打开「通知中心」查看", 6);
                    }
                  }, 3000);
                } else {
                  message.warning("未获得通知权限");
                }
              });
            } else {
              message.warning("通知权限已被拒绝，请在浏览器设置中允许通知");
            }
          }}
        >
          测试通知
        </button>
      )}

      <Header className="py-[16px] px-[24px]"></Header>
      <div className="flex-1 p-[24px] pt-0 overflow-y-auto scrollbar">
        <UICardWrap onVideoDetailClick={handleVideoDetailClick}></UICardWrap>
      </div>
      <GlobalVideoDetailModal
        open={detailModalOpen}
        records={videoGeneratedList.data}
        initialIndex={detailModalIndex}
        onClose={() => setDetailModalOpen(false)}
      />
    </div>
  );
};
